import { TBuildTarget, TProductKey, TTargetEnvironment } from '../types';
import { getBEDEV2ServiceBasePath } from './getBasePaths';
import * as localStorageUtils from './localStorage';

export type NavigationConfigsProviderProps = {
  environment: TTargetEnvironment;
  target: TBuildTarget;
  currentProduct: TProductKey;
};

enum NavigationIXPParameters {
  disableProducts = 'disableProducts',
  enableLuobu = 'enableLuobu',
  creatorEventsVariant = 'creatorEventsVariant',
  enableAssistant = 'enableAssistant',
  enableCourses = 'enableCourses',
  enableReverseDelegation = 'enableReverseDelegation',
  enableReverseDelegationShadow = 'enableReverseDelegationShadow',
}

export type TNavigationIXPResults = {
  [NavigationIXPParameters.enableAssistant]: boolean;
  [NavigationIXPParameters.disableProducts]: string[] | null;
  [NavigationIXPParameters.enableLuobu]: boolean | null;
  [NavigationIXPParameters.creatorEventsVariant]: string | null; // 'tab', 'dropdown', 'control'
  [NavigationIXPParameters.enableCourses]: boolean | null;
  [NavigationIXPParameters.enableReverseDelegation]: boolean | null;
  [NavigationIXPParameters.enableReverseDelegationShadow]: boolean | null;
};
// update storage key everytime we change ixp params setup
export const storageKey = '_navigation';

export const defaultIXPParamsValue: TNavigationIXPResults = {
  enableAssistant: false,
  disableProducts: null,
  enableLuobu: null,
  creatorEventsVariant: null,
  enableCourses: false,
  enableReverseDelegation: false,
  enableReverseDelegationShadow: false,
};

export async function fetchNavigationIXPParametersForLayer(
  layer: string,
  target: TBuildTarget,
  environment: TTargetEnvironment,
  params: Record<string, string>,
) {
  const baseURL = getBEDEV2ServiceBasePath('product-experimentation-platform', target, environment);
  const paramList = Object.values(params).join(',');
  const url = `${baseURL}/v1/projects/1/layers/${layer}/values?parameters=${paramList}`;
  const response = await fetch(url, {
    credentials: 'include',
  });
  return response.json();
}

export async function getNavigationIXPParamsUncached(
  target: TBuildTarget,
  environment: TTargetEnvironment,
  params: Record<string, string>,
) {
  const [ixpParams, ixpParamsByUser, ixpParamsByUserId, ixpParamsReverseDelegation] =
    await Promise.allSettled([
      fetchNavigationIXPParametersForLayer('CreatorHub.Navigation', target, environment, params),
      fetchNavigationIXPParametersForLayer(
        'CreatorHub.Navigation.User',
        target,
        environment,
        params,
      ),
      fetchNavigationIXPParametersForLayer(
        'CreatorHub.CreatorDocumentation.UserId',
        target,
        environment,
        { enableCourses: 'enableCourses' },
      ),
      fetchNavigationIXPParametersForLayer('CreatorHub.Creations.Permission', target, environment, {
        enableReverseDelegation: 'enableReverseDelegation',
        enableReverseDelegationShadow: 'enableReverseDelegationShadow',
      }),
    ]);

  if (
    ixpParams.status === 'fulfilled' &&
    ixpParamsByUser.status === 'fulfilled' &&
    ixpParamsByUserId.status === 'fulfilled' &&
    ixpParamsReverseDelegation.status === 'fulfilled'
  ) {
    return {
      ixpParamsValue: ixpParams.value,
      ixpParamsByUserValue: ixpParamsByUser.value,
      ixpParamsByUserIdValue: ixpParamsByUserId.value,
      ixpParamsReverseDelegationValue: ixpParamsReverseDelegation.value,
    };
  }
  return null;
}

async function getNavigationConfigsUncached(target: TBuildTarget, environment: TTargetEnvironment) {
  const res = await getNavigationIXPParamsUncached(target, environment, NavigationIXPParameters);
  if (res) {
    const {
      ixpParamsValue,
      ixpParamsByUserValue,
      ixpParamsByUserIdValue,
      ixpParamsReverseDelegationValue,
    } = res;
    return {
      disableProducts: ixpParamsValue.disableProducts || ixpParamsByUserValue.disableProducts || [],
      enableLuobu: ixpParamsValue.enableLuobu || ixpParamsByUserValue.enableLuobu || false,
      enableAssistant:
        ixpParamsValue.enableAssistant || ixpParamsByUserValue.enableAssistant || false,
      creatorEventsVariant:
        ixpParamsValue.creatorEventsVariant || ixpParamsByUserValue.creatorEventsVariant || null,
      layoutVariant: ixpParamsValue.layoutVariant || ixpParamsByUserValue.layoutVariant || null,
      enableCourses: ixpParamsByUserIdValue?.enableCourses ?? false,
      // reverse delegation ixp params
      enableReverseDelegation: ixpParamsReverseDelegationValue?.enableReverseDelegation ?? false,
      enableReverseDelegationShadow:
        ixpParamsReverseDelegationValue?.enableReverseDelegationShadow ?? false,
    };
  }
  return null;
}

export function getNavigationIXPParams(
  target: TBuildTarget,
  environment: TTargetEnvironment,
  defaultValue = defaultIXPParamsValue,
): TNavigationIXPResults {
  // read ixp configs from cache storage or fallback to default
  const configs: TNavigationIXPResults = localStorageUtils.get(storageKey) ?? defaultValue;

  // async trigger ixp fetching and update cache for next time
  getNavigationConfigsUncached(target, environment).then((result) =>
    localStorageUtils.set(storageKey, result ?? defaultValue),
  );

  return configs;
}
